import React, { useContext, useEffect, useState } from 'react';
import moment from 'moment';
import ButtonV2 from 'components/common/ButtonV2';
import { axiosGetAPI } from 'services';
import { authContext } from 'contexts/AuthContext';
import { FiChevronDown } from 'react-icons/fi';

const headerStyle = {
  padding: '10px 14px',
  textAlign: 'left',
  fontWeight: 700,
  color: '#344054',
  background: '#f2f4f7',
  borderBottom: '2px solid #e4e7ec',
  borderRight: '1px solid #e4e7ec',
  fontSize: '13px',
  whiteSpace: 'nowrap',
};

const cellStyle = {
  padding: '12px 14px',
  color: '#344054',
  verticalAlign: 'middle',
  fontSize: '13px',
  borderBottom: '1px solid #e9ecef',
  borderRight: '1px solid #e9ecef',
};

const normaliseStatus = (raw) => {
  if (!raw) return 'Pending';
  const lower = raw.toLowerCase();
  if (lower === 'approved') return 'Approved';
  if (lower === 'rejected') return 'Rejected';
  return 'Pending';
};

const formatDateTime = (timestamp) => {
  if (!timestamp) return '-';
  const num = Number(timestamp);
  let m;
  if (Number.isNaN(num)) {
    m = moment(timestamp);
  } else {
    m = num < 1e11 ? moment.unix(num) : moment(num);
  }

  if (!m.isValid()) return '-';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', lineHeight: 1.2 }}>
      <span style={{ fontWeight: 600, color: '#344054', fontSize: '12px', whiteSpace: 'nowrap' }}>
        {m.format('DD MMM YYYY')}
      </span>
      <span style={{ color: '#667085', fontSize: '11px', whiteSpace: 'nowrap' }}>
        {m.format('hh:mm A')}
      </span>
    </div>
  );
};

const RequestListView = ({
  onView,
  statusFilter = 'pending',
  source = 'admin',
  tabTitle = 'Rate Requests',
}) => {
  const { auth } = useContext(authContext);
  const authToken = auth?.data?.token;
  const [apiRequests, setApiRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRequests, setExpandedRequests] = useState({});

  const toggleRequest = (requestId) => {
    setExpandedRequests((current) => ({
      ...current,
      [requestId]: !current[requestId],
    }));
  };

  useEffect(() => {
    let isMounted = true;

    const fetchRequests = async () => {
      setLoading(true);
      try {
        const isWeb = source === 'web';
        const endpoint = isWeb
          ? `${process.env.REACT_APP_REST_API_ENDPOINT}delivery-rate/v2/web-request`
          : `${process.env.REACT_APP_REST_API_ENDPOINT}delivery-rate/v2/request`;

        const statusVal = isWeb && statusFilter ? statusFilter.toUpperCase() : statusFilter;
        const statusParam = statusVal ? `&status=${encodeURIComponent(statusVal)}` : '';
        const url = `${endpoint}?pageNumber=0&pageSize=100${statusParam}`;

        const response = await axiosGetAPI(url, { data: { token: authToken } });
        if (isMounted && response?.isSuccessful && response?.data) {
          const rawList = Array.isArray(response.data.data)
            ? response.data.data
            : Array.isArray(response.data)
              ? response.data
              : [];

          const mapped = rawList.map((req, idx) => ({
            id: req.id,
            serialNumber: req.serialNumber || idx + 1,
            origin: req.origin || req.originCityName || '-',
            destination: req.destination || req.destinationCityName || '-',
            requestedBy: req.requestedBy || req.requestedByName || 'N/A',
            requestedByName: req.requestedByName,
            requestedByMobile: req.requestedByMobile,
            vehicle: req.vehicleSizeLabel || req.vehicle || '-',
            quantity: req.quantity != null ? req.quantity : null,
            quantityUnit: req.actualQuantityUnit || 'MT',
            gradeName: req.gradeName,
            listingId: req.listingId,
            status: normaliseStatus(req.status),
            createdAt: req.createdAt,
            updatedAt: req.updatedAt,
            routeId: req.routeId || req.deliveryRouteId || req.route?.id || req.route_id || null,
            originCityId: req.originCityId || req.originCity?.id || req.originId || null,
            destinationCityId: req.destinationCityId || req.destinationCity?.id || req.destinationId || null,
            originalReq: req,
          }));
          setApiRequests(mapped);
        } else if (isMounted) {
          setApiRequests([]);
        }
      } catch (err) {
        console.error('Error fetching rate requests:', err);
        if (isMounted) setApiRequests([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchRequests();
    return () => {
      isMounted = false;
    };
  }, [authToken, statusFilter, source]);

  return (
    <div className="mx-wrap">

      {/* Table card */}
      <div className="push-notification-container rr-request-card" style={{ padding: 0, gap: 0 }}>

        {/* Title bar */}
        <div className="rr-request-card__header" style={{
          padding: '12px 16px',
          borderBottom: '1px solid #e4e7ec',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 8,
        }}>
          <span style={{ fontSize: '14px', fontWeight: 700, color: '#1d2939' }}>
            {tabTitle || (source === 'web' ? 'Web Rate Requests' : 'Rate Requests')}
          </span>
          <span style={{ fontSize: '12px', color: '#667085' }}>
            {apiRequests.length} requests{statusFilter ? ` (${statusFilter})` : ''}
          </span>
        </div>

        {/* Table */}
        <div className="push-notification__table-wrap rr-request-table-wrap" style={{ borderRadius: 0, border: 0 }}>
          <table className="push-notification__table rr-request-table" style={{ minWidth: source === 'web' ? 950 : 850 }}>
            <thead>
              <tr>
                <th style={{ ...headerStyle, width: 50 }}>S.No.</th>
                <th style={headerStyle}>Origin</th>
                <th style={headerStyle}>Destination</th>
                {source === 'web' && <th style={headerStyle}>Grade</th>}
                <th style={headerStyle}>Requested By</th>
                <th style={headerStyle}>
                  {source === 'web' ? 'Quantity (/MT)' : 'Vehicle'}
                </th>
                <th style={headerStyle}>Created At</th>
                <th style={headerStyle}>Updated At</th>
                <th style={{ ...headerStyle, width: 90, textAlign: 'center' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={source === 'web' ? 9 : 8} style={{ textAlign: 'center', padding: '40px', color: '#667085' }}>Loading requests...</td>
                </tr>
              )}
              {!loading && apiRequests.length === 0 && (
                <tr>
                  <td colSpan={source === 'web' ? 9 : 8} style={{ textAlign: 'center', padding: '40px', color: '#667085' }}>No requests found.</td>
                </tr>
              )}
              {!loading && apiRequests.length > 0 && (
                apiRequests.map((req, idx) => (
                  <tr
                    key={req.id}
                    className={expandedRequests[req.id] ? 'rr-request-row-expanded' : ''}
                    style={{ background: idx % 2 === 0 ? '#fff' : '#fafafa' }}
                  >
                    <td colSpan={source === 'web' ? 9 : 8} className="rr-request-mobile-summary">
                      <span className="rr-request-mobile-route" title={`${req.origin} → ${req.destination}`}>
                        {req.origin} <span aria-hidden="true">→</span> {req.destination}
                      </span>
                      <button
                        type="button"
                        className="rr-request-expand-button"
                        aria-label={expandedRequests[req.id] ? 'Hide request details' : 'Show request details'}
                        aria-expanded={Boolean(expandedRequests[req.id])}
                        onClick={() => toggleRequest(req.id)}
                      >
                        <FiChevronDown size={16} aria-hidden="true" />
                      </button>
                    </td>
                    <td data-label="S.No." className="rr-request-detail-cell" style={{ ...cellStyle, color: '#667085', textAlign: 'center' }}>
                      {req.serialNumber || idx + 1}
                    </td>
                    <td data-label="Origin" className="rr-request-detail-cell" style={{ ...cellStyle, fontWeight: 600 }}>
                      {req.origin}
                    </td>
                    <td data-label="Destination" className="rr-request-detail-cell" style={{ ...cellStyle, fontWeight: 600 }}>{req.destination}</td>
                    {source === 'web' && (
                      <td data-label="Grade" className="rr-request-detail-cell" style={{ ...cellStyle, fontWeight: 600, color: '#0052cc' }}>
                        {req.gradeName || '-'}
                      </td>
                    )}
                    <td data-label="Requested By" className="rr-request-detail-cell" style={cellStyle}>
                      {req.requestedByName ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', lineHeight: 1.2 }}>
                          <span style={{ fontWeight: 600, color: '#344054' }}>{req.requestedByName}</span>
                          {req.requestedByMobile && (
                            <span style={{ color: '#667085', fontSize: '11px' }}>{req.requestedByMobile}</span>
                          )}
                        </div>
                      ) : (
                        <span style={{ whiteSpace: 'pre-line' }}>{req.requestedBy}</span>
                      )}
                    </td>
                    <td data-label={source === 'web' ? 'Quantity' : 'Vehicle'} className="rr-request-detail-cell" style={cellStyle}>
                      {source === 'web' ? (
                        <span className="sr-trip-vehicle" style={{ fontSize: '12px' }}>
                          {req.quantity != null ? `${req.quantity} ${req.quantityUnit || 'KG'}` : '-'}
                        </span>
                      ) : (
                        <span className="sr-trip-vehicle" style={{ fontSize: '12px' }}>
                          {req.vehicle}
                        </span>
                      )}
                    </td>
                    <td data-label="Created At" className="rr-request-detail-cell" style={cellStyle}>{formatDateTime(req.createdAt)}</td>
                    <td data-label="Updated At" className="rr-request-detail-cell" style={cellStyle}>{formatDateTime(req.updatedAt)}</td>

                    <td data-label="Action" className="rr-request-detail-cell rr-request-action" style={{ ...cellStyle, textAlign: 'center' }}>
                      <ButtonV2
                        color="primary"
                        size="sm"
                        style={{ padding: '5px 14px', fontSize: '12px', fontWeight: 600 }}
                        onClick={() => onView(req)}
                      >
                        View
                      </ButtonV2>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

      </div>
    </div>
  );
};

export default RequestListView;


